import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { setDisplayLocale } from './locale';

/**
 * Translations live in `src/locales/<language>/`:
 *
 *   common.json                 namespace `common`, shared words ("Save", "Cancel")
 *   nursery/<Component>.json    namespace `nursery`, one file per component,
 *                               merged under the file's name. A component reads
 *                               its own with useTranslation('nursery', { keyPrefix: '<Component>' }).
 *
 * A language is a folder. Adding one is adding a folder: nothing here lists
 * them. A region (`es-ES`, `pt-BR`) holds only the words that differ from its
 * language (`es`), and i18next falls back region → language → English, key by
 * key, so a page half translated reads the rest in English rather than as keys.
 *
 * English is bundled, so the first paint and every test have it with no
 * request. Every other language is its own chunk, fetched when chosen.
 */

type Catalogue = Record<string, Record<string, unknown>>;

const english = import.meta.glob<Record<string, unknown>>('../locales/en/**/*.json', {
  eager: true,
  import: 'default',
});
const others = import.meta.glob<Record<string, unknown>>(
  ['../locales/*/**/*.json', '!../locales/en/**'],
  { import: 'default' },
);

/** `../locales/es/nursery/OrderCard.json` → { language: 'es', ns: 'nursery', key: 'OrderCard' }. */
function placeOf(path: string): { language: string; ns: string; key: string | null } {
  const parts = path.replace('../locales/', '').replace(/\.json$/, '').split('/');
  if (parts.length === 2) return { language: parts[0], ns: parts[1], key: null };
  return { language: parts[0], ns: parts[1], key: parts.slice(2).join('/') };
}

function addFile(target: Catalogue, ns: string, key: string | null, data: Record<string, unknown>) {
  target[ns] ??= {};
  if (key) target[ns][key] = data;
  else Object.assign(target[ns], data);
}

function buildEnglish(): Catalogue {
  const out: Catalogue = {};
  for (const [path, data] of Object.entries(english)) {
    const { ns, key } = placeOf(path);
    addFile(out, ns, key, data);
  }
  return out;
}

/** Every language with at least one file, English first. */
export const SHIPPED_LANGUAGES: string[] = [
  'en',
  ...Array.from(new Set(Object.keys(others).map((p) => placeOf(p).language))).sort(),
];

const STORAGE_KEY = 'factree_language';

function storedLanguage(): string {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && SHIPPED_LANGUAGES.includes(v)) return v;
  } catch {
    // Private mode, blocked storage: English.
  }
  return 'en';
}

/** The language and, for a region, its base: `es-ES` → ['es-ES', 'es']. English is never fetched. */
function chainOf(language: string): string[] {
  const base = language.split('-')[0];
  return Array.from(new Set([language, base])).filter((l) => l !== 'en');
}

const loaded = new Set<string>(['en']);

async function ensureLoaded(language: string): Promise<void> {
  await Promise.all(chainOf(language).map(async (lng) => {
    if (loaded.has(lng)) return;
    const files = Object.entries(others).filter(([p]) => placeOf(p).language === lng);
    const catalogue: Catalogue = {};
    await Promise.all(files.map(async ([path, load]) => {
      const { ns, key } = placeOf(path);
      addFile(catalogue, ns, key, await load());
    }));
    for (const [ns, data] of Object.entries(catalogue)) {
      i18n.addResourceBundle(lng, ns, data, true, true);
    }
    loaded.add(lng);
  }));
}

function applied(language: string) {
  setDisplayLocale(language);
  if (typeof document !== 'undefined') document.documentElement.lang = language;
}

i18n.use(initReactI18next).init({
  resources: { en: buildEnglish() },
  lng: 'en',
  fallbackLng: (code) => {
    const base = code?.split('-')[0];
    return base && base !== code && base !== 'en' ? [base, 'en'] : ['en'];
  },
  ns: ['common', 'nursery', 'timesheet'],
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
  returnNull: false,
  initAsync: false,
});

/**
 * Switch the app's language. Unknown codes fall back to English rather than
 * throwing: a code stored by a newer bundle must not break an older one.
 */
export async function setLanguage(language: string | null | undefined): Promise<string> {
  const next = language && SHIPPED_LANGUAGES.includes(language) ? language : 'en';
  try {
    await ensureLoaded(next);
  } catch {
    // A chunk that failed to load (offline, stale deploy) leaves the app in English.
    return i18n.language;
  }
  await i18n.changeLanguage(next);
  applied(next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Not remembered on this device; the account still is.
  }
  return next;
}

export function currentLanguage(): string {
  return i18n.language || 'en';
}

// The language this device chose last, so the first paint is already in it.
// The account's choice follows once signed in (LanguageSync).
const initial = storedLanguage();
if (initial !== 'en') void setLanguage(initial);
else applied('en');

export default i18n;
